/**
 * Container component that renders GUI elements based on container UUID
 */

import { useViewerStore } from "../store";
import type { GuiElement } from "../types";
import { GuiFolder } from "./GuiFolder";
import { GuiButton } from "./GuiButton";
import { GuiSlider } from "./GuiSlider";
import { GuiDropdown } from "./GuiDropdown";
import { GuiCheckbox } from "./GuiCheckbox";
import { GuiText } from "./GuiText";

interface GuiContainerProps {
  containerUuid: string;
}

function GuiElementRenderer({ element }: { element: GuiElement }) {
  switch (element.type) {
    case "folder":
      return <GuiFolder element={element} />;
    case "button":
      return <GuiButton element={element} />;
    case "slider":
      return <GuiSlider element={element} />;
    case "dropdown":
      return <GuiDropdown element={element} />;
    case "checkbox":
      return <GuiCheckbox element={element} />;
    case "text":
      return <GuiText element={element} />;
    default:
      return null;
  }
}

export function GuiContainer({ containerUuid }: GuiContainerProps) {
  const guiElements = useViewerStore((state) => state.guiElements);
  const guiContainerChildren = useViewerStore((state) => state.guiContainerChildren);

  const childUuids = guiContainerChildren[containerUuid];
  if (!childUuids || childUuids.size === 0) {
    return null;
  }

  // Get elements and sort by order
  const elements: GuiElement[] = [];
  for (const uuid of childUuids) {
    const element = guiElements[uuid];
    if (element) {
      elements.push(element);
    }
  }

  elements.sort((a, b) => a.order - b.order);

  return (
    <>
      {elements.map((element) => (
        <GuiElementRenderer key={element.uuid} element={element} />
      ))}
    </>
  );
}
